import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { IInvestmentWizard } from '../wizard-interface';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NbListModule, NbSelectModule } from '@nebular/theme';
import { InvestmentCategoriesService } from '../../../../../@core/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KPI } from '../../../../../@core/api';
import { Subscription } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { spendingCategoriesSection } from '../../../../../@core/api/models';
import { FormsModule } from '@angular/forms';
import { formatSearchString } from '../../../../../@core/utils/utils.service';

@Component({
  selector: 'app-wizard-investment-category',
  standalone: true,
  imports: [
    TranslateModule,
    NbSelectModule,
    AsyncPipe,
    NbListModule,
    FormsModule
  ],
  template: `
    <div class="wrapper">
      <form class="investment-category-form" autocomplete="off">
        @if (displayMode === 'list') {
          <div class="search-bar-header">
            <div class="search-input-wrapper">
              <i class="fas fa-search search-icon"></i>
              <input
                nbInput
                id="investments-category-search-input"
                type="text"
                placeholder="{{ 'Search' | translate }}"
                (keyup)="search($event)"
                class="full-width-search"
              >
            </div>
          </div>
          <nb-list class="flex-grow-1 wrapper">
            @for (ivCat of searchResults; track ivCat) {
              <nb-list-item
                [class.selected]="selectedInvestmentCategory && selectedInvestmentCategory.id === ivCat.id"
                (click)="setInvestmentCategory(ivCat)">
                {{ ivCat.name }}
              </nb-list-item>
            }
          </nb-list>
        }
        @if (displayMode === 'fields') {
          @for (section of sections; track section;  let i = $index) {
            @if (shouldShowSection(i)) {
              @if (shouldShowDirectList(i)) {
                <nb-list class="flex-grow-1 wrapper">
                  @for (category of section.categories; track category.id) {
                    <nb-list-item
                      [class.selected]="selectedInvestmentCategory && selectedInvestmentCategory.id === category.id"
                      (click)="selectCategoryFromList(category, i)">
                      @if (category.code) {
                        <span>{{ category.code }} -</span>
                      }
                      {{ category.name }}
                    </nb-list-item>
                  }
                </nb-list>
              } @else {
                <div class="form-field">
                  <label>{{ getSectionLabel(i) }}</label>
                  <nb-select fullWidth placeholder="-" (ngModelChange)="selectCategory($event, i)"
                   [(ngModel)]="sections[i].selectedCategoryId" [ngModelOptions]="{standalone: true}"
                   disabled="{{ !section.enabled }}">
                    @for (category of section.categories; track category.id) {
                      <nb-option [value]="category.id">
                        @if (category.code) {
                          <span>{{ category.code }} -</span>
                        }
                        {{ category.name }}
                      </nb-option>
                    }
                  </nb-select>
                </div>
              }
            }
          }
        }
      </form>
    </div>
  `,
  styleUrls: ['../wizard-index.component.scss', '../../../side-forms/base-form/base-form.component.scss'],
})
export class WizardInvestmentCategoryComponent implements OnInit, IInvestmentWizard, OnDestroy {
  readonly stateKey = 'INVESTMENT_CATEGORY_SELECTION';
  state = () => this.wizardService.getState(this.stateKey);

  // dependencies
  private wizardService = inject(InvestmentWizardService);
  private ivCategoryService = inject(InvestmentCategoriesService);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);

  // component state
  selectedInvestmentCategory = this.state();
  public displayMode: 'list' | 'fields' = 'fields';
  public sections: spendingCategoriesSection[] = [];
  public searchResults: KPI.InvestmentCategory[] = [];
  private suggestedSpendingCategories: KPI.InvestmentCategory[] = []
  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    if (this.wizardService.existingInvestment) {
      this.subscriptions.push(
        this.ivCategoryService.initInvestmentCategories(this.wizardService.existingInvestment)
          .subscribe(({ sections, suggestedSpendingCategories }) => {
            this.sections = sections;
            this.suggestedSpendingCategories = suggestedSpendingCategories;
            if (this.sections.length === 1) {
              this.displayMode = 'list';
            } else {
              this.displayMode = 'fields';
            }
            if (this.state()) {
              this.initWithSelectedCategory(this.state());
            }
            const lastLevelCategories = this.sections[this.sections.length - 1].categories;
            if (this.sections.length === 1) {
              this.displayMode = 'list';
            }
            if (this.wizardService.getState('ASSET_SELECTION') || this.wizardService.getState('NOMENCLATURE_TECHNIQUE_SELECTION') || this.sections.length === 1) {
              // If we have a technical nomenclature, we look for matches
              if (this.suggestedSpendingCategories.length === 0) {
                // If no mapping for the technical nomenclature, show all
                // spending categories (the last level, I guess)
                this.suggestedSpendingCategories = lastLevelCategories;
              }
            } else {
              // If no technical nomenclature, show all spending categories
              // (the last level, I guess)
              this.suggestedSpendingCategories = lastLevelCategories;
            }
            this.searchResults = this.suggestedSpendingCategories;
          })
      )
    }
    this.ivCategoryService.toogleDisplayMode$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((toggleMode) => {
      if (toggleMode) {
        if (this.displayMode === 'fields') {
          this.displayMode = 'list';
        } else {
          this.displayMode = 'fields';
        }
      }
    })
  }

  setInvestmentCategory(category: KPI.InvestmentCategory) {
    this.wizardService.setState(this.stateKey, category);
    this.selectedInvestmentCategory = category;
    this.wizardService.saveAndClose$.next(true);
  }

  selectCategoryFromList(category: KPI.InvestmentCategory, i: number) {
    this.selectCategory(category.id, i);
    if (!category.children?.length) {
      this.wizardService.saveAndClose$.next(true);
    }
  }

  private initWithSelectedCategory(category: KPI.InvestmentCategory) {
    let levelCategory = category;
    for (let i = this.sections.length - 1; i >= 0; i--) {
      this.selectCategory(null, i, levelCategory);
      if (i !== 0 && levelCategory.parent_id) {
        // If there is a level above, pick the category's parent for this level
        const parent = this.sections[i - 1].categories.find(cat => cat.id === levelCategory.parent_id);
        if (parent) {
          levelCategory = parent;
        } else {
          console.error("parent category not found", levelCategory.parent_id);
        }
      }
    }
  }

  public selectCategory(event, i: number, investmentCategory?: KPI.InvestmentCategory) {
    let category;
    if (investmentCategory) {
      category = investmentCategory;
    } else {
      const categoryId = event;
      category = this.sections[i].categories.find(cat => cat.id === categoryId);
    }
    this.sections[i].selectedCategory = category;
    this.sections[i].selectedCategoryId = category.id;
    if (event) {
      this.resetChildSections(i + 1);
    }
    if (category.children?.length > 0) {
      this.sections[i + 1].categories = category.children;
      this.sections[i + 1].enabled = true;
    } else {
      this.wizardService.setState(this.stateKey, category);
      this.selectedInvestmentCategory = category;
    }
  }

  public shouldShowSection(i: number): boolean {
    return i === 0 || this.sections[i].enabled || !!this.sections[i].selectedCategoryId;
  }

  public shouldShowDirectList(i: number): boolean {
    return this.shouldShowSection(i) && this.sections.filter((_, sectionIndex) => this.shouldShowSection(sectionIndex)).length === 1;
  }

  public getSectionLabel(i: number) {
    return this.translate.instant("Level") + " " + (i + 1);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private resetChildSections(sectionIndex: number) {
    for(let i = sectionIndex; i < this.sections.length; i++) {
      this.sections[i].enabled = false;
      this.sections[i].categories = [];
      this.sections[i].selectedCategory = null;
      this.sections[i].selectedCategoryId = null;
    }
  }

  public search(event) {
    let searchText = event.target.value;
    if (searchText) {
      const formattedSearchText = formatSearchString(searchText);
      this.searchResults = this.suggestedSpendingCategories.filter(category => {
        const formattedCategoryName = formatSearchString(category.name);
        return formattedCategoryName.includes(formattedSearchText);
      });
    } else {
      this.searchResults = this.suggestedSpendingCategories;
    }
  }

  public onSearchCancelled() {
    this.searchResults = this.suggestedSpendingCategories;
  }
}
